import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithTokenRefresh } from "../fetchWithTokenRefresh";

// No provider-side timeout exists on the backend's speech adapters (OpenAI/Deepgram), so a
// hung upstream call would otherwise hang this fetch indefinitely.
const TRANSCRIBE_TIMEOUT_MS = 20_000;
const PREFERRED_MIME_TYPE = "audio/webm;codecs=opus";

export interface UseSpeechToTextOptions {
  // Kept for API compatibility / self-documentation; forwarded nowhere. The backend derives
  // the language hint from the Accept-Language header (part of buildHeaders' output), not
  // from any value passed through this hook.
  lang: string;
  endpoint: string;
  buildHeaders: () => Record<string, string>;
  /** Re-exchanges a fresh RPT for the current property — see property-provider.tsx. */
  refreshToken: () => Promise<string>;
  onFinalResult: (transcript: string) => void;
  onError?: (error: string) => void;
}

export interface UseSpeechToTextResult {
  isSupported: boolean;
  isListening: boolean;
  isTranscribing: boolean;
  toggle: () => void;
}

function isMediaRecordingSupported(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  return !!navigator.mediaDevices?.getUserMedia && typeof window.MediaRecorder !== "undefined";
}

function pickMimeType(): string | undefined {
  if (
    typeof MediaRecorder !== "undefined" &&
    MediaRecorder.isTypeSupported?.(PREFERRED_MIME_TYPE)
  ) {
    return PREFERRED_MIME_TYPE;
  }
  return undefined;
}

function mapGetUserMediaError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : undefined;
  if (name === "NotAllowedError" || name === "PermissionDeniedError") return "not-allowed";
  if (name === "NotFoundError" || name === "DevicesNotFoundError") return "no-mic";
  return "unknown";
}

function mapHttpError(status: number): string {
  return status === 502 ? "transcription-failed" : "unknown";
}

// Records a single utterance with MediaRecorder and sends it to the assistant backend for
// transcription when the recording stops (batch mode — no interim results). Public shape is
// kept close to the previous Web-Speech-API-based hook so ai-assistant-sidebar.tsx barely
// changes.
export function useSpeechToText({
  endpoint,
  buildHeaders,
  refreshToken,
  onFinalResult,
  onError,
}: UseSpeechToTextOptions): UseSpeechToTextResult {
  const [isListening, setIsListening] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Latest values live in refs so an in-flight recording/upload never has to be torn down
  // just because a prop changed on a re-render.
  const endpointRef = useRef(endpoint);
  const buildHeadersRef = useRef(buildHeaders);
  const refreshTokenRef = useRef(refreshToken);
  const onFinalResultRef = useRef(onFinalResult);
  const onErrorRef = useRef(onError);
  endpointRef.current = endpoint;
  buildHeadersRef.current = buildHeaders;
  refreshTokenRef.current = refreshToken;
  onFinalResultRef.current = onFinalResult;
  onErrorRef.current = onError;

  const supportRef = useRef<boolean | undefined>(undefined);
  if (supportRef.current === undefined) supportRef.current = isMediaRecordingSupported();
  const isSupported = supportRef.current;

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }, []);

  const handleStop = useCallback(() => {
    const recorder = recorderRef.current;
    const mimeType = recorder?.mimeType || PREFERRED_MIME_TYPE;
    stopStream();
    recorderRef.current = null;
    setIsListening(false);

    const chunks = chunksRef.current;
    chunksRef.current = [];
    const blob = new Blob(chunks, { type: mimeType });

    // An instant tap-and-release before any audio data arrives isn't a failure worth
    // surfacing — mirrors the old hook's quiet handling of "no-speech"/"aborted".
    if (blob.size === 0) return;

    setIsTranscribing(true);
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, TRANSCRIBE_TIMEOUT_MS);

    fetchWithTokenRefresh(
      endpointRef.current,
      {
        method: "POST",
        headers: { ...buildHeadersRef.current(), "Content-Type": mimeType },
        body: blob,
        signal: controller.signal,
      },
      refreshTokenRef.current
    )
      .then(response => {
        if (!response.ok) {
          onErrorRef.current?.(mapHttpError(response.status));
          return null;
        }
        // A malformed body on an otherwise-200 response is a distinct failure from the
        // fetch itself failing, so it's caught here rather than falling into the outer
        // catch (which would misreport it as a connectivity problem).
        return (response.json() as Promise<{ transcript?: string }>).catch(() => {
          onErrorRef.current?.("unknown");
          return null;
        });
      })
      .then(data => {
        if (data && typeof data.transcript === "string" && data.transcript.trim()) {
          onFinalResultRef.current(data.transcript);
        }
      })
      .catch(err => {
        if (err instanceof DOMException && err.name === "AbortError") {
          // Only a timeout-triggered abort is a real failure; an unmount/panel-close abort
          // is intentional cleanup and shouldn't call onError into a gone component.
          if (timedOut) onErrorRef.current?.("transcription-failed");
          return;
        }
        onErrorRef.current?.("network");
      })
      .finally(() => {
        clearTimeout(timeoutId);
        abortControllerRef.current = null;
        setIsTranscribing(false);
      });
  }, [stopStream]);

  const toggle = useCallback(() => {
    if (isTranscribing) return;

    if (isListening) {
      recorderRef.current?.stop();
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        streamRef.current = stream;
        chunksRef.current = [];
        const mimeType = pickMimeType();
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
        recorder.ondataavailable = event => {
          if (event.data.size > 0) chunksRef.current.push(event.data);
        };
        recorder.onstop = handleStop;
        recorderRef.current = recorder;
        recorder.start();
        setIsListening(true);
      })
      .catch(err => {
        onErrorRef.current?.(mapGetUserMediaError(err));
      });
  }, [isListening, isTranscribing, handleStop]);

  useEffect(() => {
    return () => {
      stopStream();
      recorderRef.current = null;
      chunksRef.current = [];
      abortControllerRef.current?.abort();
    };
  }, [stopStream]);

  return { isSupported, isListening, isTranscribing, toggle };
}
